import React, { useState } from "react";
import {
  quantityOptions,
  stateOptions,
  PHONE,
  WHATSAPP_URL,
} from "../data/content";
import "./OrderForm.css";

const INITIAL_FORM = {
  name: "",
  phone: "",
  address: "",
  qty: "1",
  state: "Telangana",
};

export default function OrderForm({ onOrderPlaced, selectedProduct, totalSales = 0 }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  function validate() {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Full name is required.";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required.";
    if (!form.address.trim())
      newErrors.address = "Delivery address is required.";
    return newErrors;
  }

  function handleSubmit() {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const productLine = selectedProduct
      ? `🛍️ *Product:* ${selectedProduct.title} (${selectedProduct.subtitle})\n` +
        (selectedProduct.packSize
          ? `📦 *Pack Size:* ${selectedProduct.packSize}\n`
          : "") +
        (selectedProduct.price
          ? `💰 *Price:* ${selectedProduct.price}\n`
          : "")
      : "";

    const msg = encodeURIComponent(
      `🌿 *New Order – Swathi's Wellness Products*\n\n` +
        productLine +
        `👤 *Name:* ${form.name}\n` +
        `📱 *Phone:* ${form.phone}\n` +
        `📦 *Quantity:* ${form.qty} Pack(s)\n` +
        `📍 *State:* ${form.state}\n` +
        `🏠 *Address:* ${form.address}\n\n` +
        `Please confirm my order and share payment details. Thank you!`,
    );

    onOrderPlaced?.({ quantity: Number(form.qty || 1), selectedProduct });

    setTimeout(() => {
      window.open(`${WHATSAPP_URL}?text=${msg}`, "_blank");
    }, 600);
  }

  return (
    <section className="order-section" id="order">
      <div className="order-wrapper">
        {/* Left Info */}
        <div className="order-left">
          <span className="section-eyebrow order-eyebrow">
            Place Your Order
          </span>
          <h2 className="section-title order-title">
            Make a Natural
            <br />
            Choice Today
          </h2>
          <p className="order-desc">
            Join hundreds of families across India who have made Swathi's
            Diabetic Wellness Powder part of their daily wellness routine. Fresh
            stock, fast delivery.
          </p>
          <div className="order-features">
            <div className="order-feature">
              <span>📦</span> Fast delivery across India
            </div>
            <div className="order-feature">
              <span>💯</span> 100% Satisfaction Guarantee
            </div>
            <div className="order-feature">
              <span>🌿</span> Freshly made natural formula
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div className="order-right">
          <div className="order-form">
            {selectedProduct ? (
              <div className="selected-product-banner">
                <div>
                  <div className="selected-product-title">
                    Ordering: {selectedProduct.title}
                  </div>
                  <div className="selected-product-subtitle">
                    {selectedProduct.subtitle}
                  </div>
                </div>
                <div className="selected-product-price">
                  {(() => {
                    const asStr = selectedProduct.price;
                    const match = String(asStr)
                      .replace(/,/g, "")
                      .match(/\d+(?:\.\d+)?/);
                    const value = match ? Number(match[0]) : 0;
                    const formatted = `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
                    return formatted;
                  })()}
                </div>
              </div>
            ) : (
              <div className="selected-product-banner empty">
                Select a product from above or choose any pack here.
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <span className="form-error">{errors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+91 XXXXX XXXXX"
                  value={form.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <span className="form-error">{errors.phone}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Delivery Address *</label>
              <textarea
                id="address"
                name="address"
                rows={2}
                placeholder="House No., Street, Area, City, Pincode"
                value={form.address}
                onChange={handleChange}
              />
              {errors.address && (
                <span className="form-error">{errors.address}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="qty">Quantity</label>
                <select
                  id="qty"
                  name="qty"
                  value={form.qty}
                  onChange={handleChange}
                >
                  {quantityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                >
                  {stateOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sales-summary">
              <span className="sales-summary__badge">📈 Total Sales</span>
              <strong>{totalSales} units sold</strong>
            </div>

            <button className="submit-btn" onClick={handleSubmit}>
              🛒 Place Order via WhatsApp
            </button>

            <p className="whatsapp-alt">
              Or call directly: <a href={`tel:${PHONE}`}>{PHONE}</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
